import React, { useState } from 'react';
import { ChevronDownIcon, ClockIcon, UsersIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
import { CRESTMONT } from './CrestmontChrome';
import { CRESTMONT_ACCREDITORS, type BenefitCard, type FaqEntry } from '../../data/crestmontPrograms';
const BENEFIT_ICONS = {
  clock: ClockIcon,
  users: UsersIcon,
  shield: ShieldCheckIcon,
  globe: GlobeIcon
} as const;
export function CtaBand({
  heading,
  body,
  onApply
}: {
  heading: string;
  body: string;
  onApply: () => void;
}) {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CRESTMONT.navy
  }} data-id="element-328" data-mp="18q1ynghqhyf2">
      {/* Gold wedge from the brand guide — decorative, hidden on mobile */}
      <div className="hidden md:block absolute inset-y-0 right-0 w-1/4" style={{
      backgroundColor: CRESTMONT.gold,
      clipPath: 'polygon(28% 0, 100% 0, 100% 100%, 0% 100%)'
    }} aria-hidden="true" data-id="element-329" data-mp="yzh17wdsqzxq" />
      <div className="relative max-w-6xl mx-auto px-4 py-10 flex flex-col md:flex-row md:items-center gap-6" data-id="element-330" data-mp="2pupjelkbt0lu">
        <div className="md:max-w-2xl" data-id="element-331" data-mp="2kvuxhar2avxi">
          <h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight" data-id="element-332" data-mp="2kx64djft0jsu">
            {heading}
          </h2>
          <p className="mt-2 text-sm text-white/70 leading-relaxed" data-id="element-333" data-mp="2zmltkuzosh6f">{body}</p>
        </div>
        <button onClick={onApply} className="md:ml-auto flex-shrink-0 px-6 py-3 rounded text-sm font-bold bg-white transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" style={{
        color: CRESTMONT.navy
      }} data-id="element-334" data-mp="2ushhjxbkdiyh">
          Apply Now
        </button>
      </div>
    </section>;
}
export function BenefitsSection({
  benefits
}: {
  benefits: BenefitCard[];
}) {
  return <section className="py-16 md:py-20" style={{
    backgroundColor: CRESTMONT.paper
  }} aria-labelledby="benefits-heading" data-id="element-335" data-mp="1o65a8ql0hgea">
      <div className="max-w-5xl mx-auto px-4" data-id="element-336" data-mp="28uejfi6zct9c">
        <h2 id="benefits-heading" className="text-center text-2xl md:text-3xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-337" data-mp="28vg50glmgy5b">
          Advance your career without pausing your life.
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600" data-id="element-338" data-mp="2mu63o0o3b5vs">
          The four things Crestmont students say made the difference.
        </p>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-339" data-mp="2rs7keey1mt43">
          {benefits.map(benefit => {
          const Icon = BENEFIT_ICONS[benefit.icon];
          return <li key={benefit.title} className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col" data-id="element-340" data-mp="30mm02imhk5nq">
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: CRESTMONT.navy
            }} data-id="element-341" data-mp="3ajqpcrbmoh8r">
                  <Icon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-342" data-mp="3fgh49bbp56sl" />
                </span>
                <p className="mt-4 text-[11px] font-bold uppercase tracking-wide text-gray-500" data-id="element-343" data-mp="13t57k4bdl0o0">
                  {benefit.eyebrow}
                </p>
                <p className="mt-auto pt-1.5 text-sm font-bold leading-snug" style={{
              color: CRESTMONT.navy
            }} data-id="element-344" data-mp="tyd8sx6247rn">
                  {benefit.title}
                </p>
              </li>;
        })}
        </ul>

        <div className="mt-12 pt-10 border-t border-gray-200" data-id="element-345" data-mp="1hn66oz68opow">
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500" data-id="element-346" data-mp="1hozh8pjz3u26">
            Accreditation & authorization
          </p>
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-347" data-mp="17w1wsdvf7v0x">
            {CRESTMONT_ACCREDITORS.map(accreditor => <li key={accreditor.abbr} className="text-center" data-id="element-348" data-mp="25ejzf68aynae">
                <span className="block text-lg font-extrabold tracking-tight" style={{
              color: CRESTMONT.navy
            }} data-id="element-349" data-mp="20kz3rku6m5ed">
                  {accreditor.abbr}
                </span>
                <span className="mt-1 block text-[10px] leading-snug text-gray-500" data-id="element-350" data-mp="1q0r5l1jxdzr7">
                  {accreditor.name}
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}
function FaqItem({
  entry,
  index
}: {
  entry: FaqEntry;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-351" data-mp="28sa8l8dy9dt8">
      <button type="button" onClick={() => setIsOpen(prev => !prev)} aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} className="w-full py-4 flex items-start justify-between gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b] rounded" data-id="element-352" data-mp="28v65quhz3ygm">
        <span className="text-sm font-bold" style={{
        color: CRESTMONT.navy
      }} data-id="element-353" data-mp="2rkcq0lyf0y4j">
          {entry.question}
        </span>
        <ChevronDownIcon className={`mt-0.5 w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-354" data-mp="g2zqis8n2gvn" />
      </button>
      {isOpen && <p id={`faq-answer-${index}`} className="pb-4 pr-8 text-sm leading-relaxed text-gray-600" data-id="element-355" data-mp="kwlvrzch3ihf">
          {entry.answer}
        </p>}
    </div>;
}
export function FaqSection({
  faq
}: {
  faq: FaqEntry[];
}) {
  return <section className="py-16 md:py-20 bg-white" aria-labelledby="faq-heading" data-id="element-356" data-mp="66oe8291tr17">
      <div className="max-w-3xl mx-auto px-4" id="faq" data-id="element-357" data-mp="64k2iy4wrnqd">
        <h2 id="faq-heading" className="text-center text-2xl md:text-3xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-358" data-mp="2403tq4osc24">
          Frequently Asked Questions
        </h2>
        <div className="mt-8" data-id="element-359" data-mp="1djyj4bqgjubs">
          {faq.map((entry, index) => <FaqItem key={entry.question} entry={entry} index={index} data-id="element-360" data-mp="a89c91r1rupr" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}